import { ref, computed, shallowRef, markRaw } from "vue";
import { useStore } from "@/store/Expedientes/pacientes.js";
import { updatePatientData } from "@/utils/eventBus.js";
import {
  guardarVistaDelPaciente,
  restaurarVistaDelPaciente,
  guardarEstadoEdicion,
  restaurarEstadoEdicion,
  limpiarEstadoEdicion
} from '@/utils/persistenceUtils';

/**
 * Composable para manejar el estado global del Workspace Médico (Medical Workspace)
 * Este composable centraliza la comunicación entre todos los componentes
 * evitando conflictos y manteniendo un flujo de datos unidireccional
 *
 * OPTIMIZACIONES DE RENDIMIENTO:
 * - Uso de shallowRef para objetos grandes (datosPaciente)
 * - Memoización de computed pesados
 * - Debounce/throttle en funciones frecuentes
 * - Eliminación completa de console.log para mejor rendimiento
 */

// ⚡ OPTIMIZACIÓN: Utilidad para debounce
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// ⚡ OPTIMIZACIÓN: Utilidad para throttle
const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// 🔄 Las funciones de persistencia ahora están importadas desde utils/persistenceUtils.js

// Estado compartido entre todas las instancias
const state = {
  // Estado de los paneles
  isLeftPanelCollapsed: ref(true),
  isRightPanelCollapsed: ref(true),
  isDetailPanelExpanded: ref(false),
  detailExpansionLevel: ref(0), // 0: normal, 1: expandido en 2 cols, 2: pantalla completa

  // Estado de la nota/detalle seleccionado
  // ⚡ OPTIMIZACIÓN: markRaw para evitar reactividad profunda innecesaria
  detalleSeleccionado: ref(null),

  // Estado de filtros
  // filtroActual: Filtro simple rápido (ej: 'recientes', 'mes', 'fecha')
  filtroActual: ref(""),
  // filtroAvanzado: Filtro combinado con múltiples criterios:
  //   - tipo: 'recientes' | 'mes' | 'fecha'
  //   - documento: 'todo' | 'nota-medica' | 'receta' | 'reporte' | etc.
  //   - mes: 'YYYY-MM' (para tipo 'mes')
  //   - inicio/fin: 'YYYY-MM-DD' (para tipo 'fecha')
  filtroAvanzado: ref(null),

  // Estado del paciente
  patientId: ref(null),

  // 🆕 DATOS COMPLETOS DEL PACIENTE
  // ⚡ OPTIMIZACIÓN: shallowRef para objetos grandes - solo reacciona a cambio de referencia
  datosPaciente: shallowRef({
    // Datos básicos del paciente (estructura real de la BD)
    id: null,
    first_name: "",
    last_name: "",
    gender: "",
    birthdate: "",
    email: "",
    whatsapp: "",
    // Datos del perfil extendido (desde PatientPerfil.vue)
    profile: {
      address: "",
      reference: "",
      responsible_family_member: "",
      family_member_relationship: "",
      emergency_contact: "",
      patient_identification: "",
      internal_comments: "",
      patient_feedback: "",
      updated_at: null,
    },
    historiaClinica: {
      antecedentesPersonales: [],
      antecedentesFamiliares: [],
      alergias: [],
      medicamentosActuales: [],
      cirugiasPrevias: [],
      enfermedadesCronicas: [],
    },
    notasMedicas: [], // Array de notas médicas
    recetas: [], // Array de recetas
    documentos: [], // Array de documentos (laboratorios, imagenología, etc.)
    signosVitales: [], // Array de registros de signos vitales
  }),

  // Estado de modo de acción (nuevo, editar, ver)
  modoAccion: ref(null), // 'nueva-nota', 'nueva-receta', 'documentos', 'signos-vitales', 'alergias', 'ver-historial', 'historia-clinica', 'alertas-paciente', 'perfil-paciente', 'expediente-completo'

  // Vista activa en el panel central
  vistaActiva: ref("timeline"), // 'timeline', 'expediente-completo', 'formulario'

  // Datos temporales para edición
  datosEdicion: ref(null), // { tipo, datos, id }

  // Sistema de navegación tipo libro - Historial de páginas visitadas
  // ⚡ OPTIMIZACIÓN: shallowRef para arrays - más eficiente para listas grandes
  historialNavegacion: shallowRef([]), // Array de objetos { modo, timestamp, titulo }
  indiceNavegacionActual: ref(-1), // Índice actual en el historial (-1 = sin historial)

  // ⚡ OPTIMIZACIÓN: Cache para computed pesados
  _layoutClassesCache: null,
  _lastLayoutState: null,
};

// ⚡ OPTIMIZACIÓN: Watcher eliminado para mejorar rendimiento en producción

export function useMedicalWorkspace() {
  // 🆕 COMPUTED: Verificar si el paciente tiene contenido
  const pacienteTieneNotas = computed(() => {
    return state.datosPaciente.value.notasMedicas.length > 0;
  });

  const pacienteTieneRecetas = computed(() => {
    return state.datosPaciente.value.recetas.length > 0;
  });

  const pacienteTieneDocumentos = computed(() => {
    return state.datosPaciente.value.documentos.length > 0;
  });

  const pacienteTieneContenido = computed(() => {
    return (
      pacienteTieneNotas.value ||
      pacienteTieneRecetas.value ||
      pacienteTieneDocumentos.value
    );
  });

  const esPacienteNuevo = computed(() => {
    return !pacienteTieneContenido.value;
  });

  // ⚡ OPTIMIZACIÓN: Computed memoizado para layout - cachea resultado si estado no cambió
  const layoutClasses = computed(() => {
    // ⚡ OPTIMIZACIÓN: Crear clave única del estado para cache
    const currentState = {
      leftCollapsed: state.isLeftPanelCollapsed.value,
      rightCollapsed: state.isRightPanelCollapsed.value,
      detailExpanded: state.isDetailPanelExpanded.value,
      modoAccion: state.modoAccion.value,
      hasDetalle: !!state.detalleSeleccionado.value,
    };

    // ⚡ OPTIMIZACIÓN: Verificar si el estado cambió para usar cache
    const stateKey = JSON.stringify(currentState);
    if (state._lastLayoutState === stateKey && state._layoutClassesCache) {
      return state._layoutClassesCache;
    }

    const classes = {
      "left-collapsed": state.isLeftPanelCollapsed.value,
      "right-collapsed": state.isRightPanelCollapsed.value,
      "detail-expanded": state.isDetailPanelExpanded.value,
    };

    // � EXPERIENCIA AVANZADA DE USUARIO - PANELES INTELIGENTES
    // Los paneles se mantienen abiertos para facilitar la navegación del médico

    // CASO 1: Historia Clínica - SIEMPRE con BlocAcciones visible para navegación rápida
    if (state.modoAccion.value === "historia-clinica") {
      classes["modo-expediente-completo"] = true;
      state._lastLayoutState = stateKey;
      state._layoutClassesCache = classes;
      return classes;
    }

    // CASO 2: Perfil del Paciente - BlocAcciones visible para navegación contextual
    if (state.modoAccion.value === "perfil-paciente") {
      // Usar modo con 3 columnas: BlocAcciones + Timeline + Perfil
      classes["modo-perfil-con-navegacion"] = true;
      state._lastLayoutState = stateKey;
      state._layoutClassesCache = classes;
      return classes;
    }

    // CASO 3: Expediente Completo / Ver Historial - Máxima información disponible
    if (
      state.modoAccion.value === "ver-historial" ||
      state.modoAccion.value === "expediente-completo"
    ) {
      classes["modo-expediente-completo"] = true;
      state._lastLayoutState = stateKey;
      state._layoutClassesCache = classes;
      return classes;
    }

    // CASO 4: Detalle expandido - Permitir BlocAcciones si el usuario lo activa manualmente
    if (state.isDetailPanelExpanded.value) {
      // Respetar la decisión del usuario sobre BlocAcciones
      state._lastLayoutState = stateKey;
      state._layoutClassesCache = classes;
      return classes;
    }

    // CASO 5: Otros modos de acción (nueva nota, receta, etc)
    if (state.modoAccion.value) {
      // Mantener BlocAcciones visible para navegación rápida entre funciones
      if (!state.isLeftPanelCollapsed.value) {
        classes["modo-formulario-con-acciones"] = true;
      } else {
        // Solo si el usuario explícitamente cerró el panel
        classes["modo-formulario-activo"] = true;
      }
    } else if (state.detalleSeleccionado.value) {
      // Modo detalle: comportamiento inteligente según panel izquierdo
      if (state.isLeftPanelCollapsed.value) {
        // Panel izquierdo oculto: Timeline (33%) + Detalle (67%)
        classes["modo-detalle-activo"] = true;
      } else {
        // Panel izquierdo visible: Las 3 columnas visibles
        classes["modo-detalle-con-acciones"] = true;
      }
    } else {
      // Modo normal: las 3 columnas visibles
      classes["modo-normal"] = true;
    }

    // ⚡ OPTIMIZACIÓN: Guardar en cache
    state._lastLayoutState = stateKey;
    state._layoutClassesCache = classes;
    return classes;
  });

  // ⚡ OPTIMIZACIÓN: Métodos con throttle para evitar llamadas excesivas
  // Métodos para manejar paneles
  const togglePanel = throttle((panel) => {
    if (panel === "left") {
      state.isLeftPanelCollapsed.value = !state.isLeftPanelCollapsed.value;
      // ⚡ OPTIMIZACIÓN: Invalidar cache de layout
      state._layoutClassesCache = null;
      state._lastLayoutState = null;
    } else if (panel === "right") {
      state.isRightPanelCollapsed.value = !state.isRightPanelCollapsed.value;
      // ⚡ OPTIMIZACIÓN: Invalidar cache de layout
      state._layoutClassesCache = null;
      state._lastLayoutState = null;
      // Si se cierra el panel derecho, limpiar la selección
      if (state.isRightPanelCollapsed.value) {
        state.detalleSeleccionado.value = null;
      }
    }
  }, 100); // Throttle de 100ms para evitar múltiples toggles rápidos

  const collapsePanel = (panel) => {
    if (panel === "left") {
      state.isLeftPanelCollapsed.value = true;
    } else if (panel === "right") {
      state.isRightPanelCollapsed.value = true;
    }
  };

  const expandPanel = (panel) => {
    if (panel === "left") {
      state.isLeftPanelCollapsed.value = false;
    } else if (panel === "right") {
      state.isRightPanelCollapsed.value = false;
    }
  };

  // Métodos para manejar el detalle
  const seleccionarNota = (nota) => {
    // Limpiar modo de acción al seleccionar una nota
    state.modoAccion.value = null;
    state.vistaActiva.value = "detalle";

    // ⚡ OPTIMIZACIÓN: markRaw para evitar reactividad profunda innecesaria en objetos grandes
    state.detalleSeleccionado.value =
      nota && typeof nota === "object" ? markRaw(nota) : nota;
    // Timeline (50%) + Detalle (50%), ocultar panel izquierdo
    state.isLeftPanelCollapsed.value = true;
    state.isRightPanelCollapsed.value = false;
    state.isDetailPanelExpanded.value = false;
    state.detailExpansionLevel.value = 0; // Resetear nivel de expansión
    // ⚡ OPTIMIZACIÓN: Invalidar cache
    state._layoutClassesCache = null;
    state._lastLayoutState = null;
  };

  const cerrarDetalle = () => {
    state.detalleSeleccionado.value = null;
    state.modoAccion.value = null;
    state.vistaActiva.value = "normal";
    // Volver a modo normal: mostrar las 3 columnas
    state.isLeftPanelCollapsed.value = true;
    state.isRightPanelCollapsed.value = true;
    state.isDetailPanelExpanded.value = false;
    state.detailExpansionLevel.value = 0;
  };

  // ⚡ OPTIMIZACIÓN: Throttle para toggleDetailExpansion
  const toggleDetailExpansion = throttle(() => {
    // Expansión simple: toggle entre normal y pantalla completa
    const isInDetailMode =
      state.detalleSeleccionado.value && !state.modoAccion.value;

    if (isInDetailMode) {
      // Toggle simple: expandir o contraer
      state.isDetailPanelExpanded.value = !state.isDetailPanelExpanded.value;

      // NO forzar el estado de isLeftPanelCollapsed
      // Dejar que el usuario controle el panel de acciones manualmente
      // El CSS se encargará de ocultar/mostrar según isDetailPanelExpanded

      // ⚡ OPTIMIZACIÓN: Invalidar cache
      state._layoutClassesCache = null;
      state._lastLayoutState = null;

      // Resetear nivel de expansión (ya no se usa la lógica progresiva)
      state.detailExpansionLevel.value = state.isDetailPanelExpanded.value
        ? 1
        : 0;
    } else {
      // Caso por defecto para otros modos: toggle simple
      state.isDetailPanelExpanded.value = !state.isDetailPanelExpanded.value;
      // NO forzar isLeftPanelCollapsed - dejar que el usuario lo controle
    }

    // ⚡ OPTIMIZACIÓN: Invalidar cache
    state._layoutClassesCache = null;
    state._lastLayoutState = null;
  }, 150); // Throttle de 150ms

  // ⚡ OPTIMIZACIÓN: Métodos para manejar filtros
  const aplicarFiltro = (tipoFiltro) => {
    state.filtroActual.value = tipoFiltro;
    state.filtroAvanzado.value = null; // Limpiar filtro avanzado
    state.vistaActiva.value = "timeline";
  };

  const aplicarFiltroAvanzado = debounce((filtroData) => {
    state.filtroAvanzado.value = filtroData;
    state.filtroActual.value = ""; // Limpiar filtro simple
    state.vistaActiva.value = "timeline";
  }, 200);

  const limpiarFiltros = () => {
    state.filtroActual.value = "";
    state.filtroAvanzado.value = null;
  };

  // Métodos para el sistema de navegación tipo libro
  const obtenerTituloModo = (modo) => {
    const titulos = {
      "perfil-paciente": "Perfil del Paciente",
      "historia-clinica": "Historia Clínica",
      "nueva-nota": "Nueva Nota Médica",
      "nueva-receta": "Nueva Receta",
      "nuevo-reporte": "Reporte con Imágenes",
      documentos: "Documentos",
      "signos-vitales": "Signos Vitales",
      alergias: "Alergias",
      "expediente-completo": "Expediente Completo",
      "ver-historial": "Historial",
      "alertas-paciente": "Alertas",
      "notas-medicas": "Notas Médicas",
      recetas: "Recetas",
      evolucion: "Evolución",
    };
    return titulos[modo] || modo;
  };

  const agregarAlHistorial = (modo, titulo = "") => {
    // Si estamos navegando hacia atrás/adelante, no modificar el historial
    if (
      state.indiceNavegacionActual.value >= 0 &&
      state.indiceNavegacionActual.value <
      state.historialNavegacion.value.length - 1
    ) {
      // Eliminar elementos futuros si estamos en medio del historial
      state.historialNavegacion.value = state.historialNavegacion.value.slice(
        0,
        state.indiceNavegacionActual.value + 1
      );
    }

    const entrada = {
      modo,
      titulo: titulo || obtenerTituloModo(modo),
      timestamp: Date.now(),
      detalleSeleccionado: state.detalleSeleccionado.value
        ? { ...state.detalleSeleccionado.value }
        : null,
    };

    // 🔥 CRÍTICO: Cambiar referencia del array para que shallowRef detecte el cambio
    state.historialNavegacion.value = [...state.historialNavegacion.value, entrada];
    state.indiceNavegacionActual.value =
      state.historialNavegacion.value.length - 1;

    // Limitar historial a 50 elementos (también cambiar referencia)
    if (state.historialNavegacion.value.length > 50) {
      state.historialNavegacion.value = state.historialNavegacion.value.slice(1);
      state.indiceNavegacionActual.value--;
    }
  };

  const navegarAtras = () => {
    if (state.indiceNavegacionActual.value > 0) {
      state.indiceNavegacionActual.value--;
      const entrada =
        state.historialNavegacion.value[state.indiceNavegacionActual.value];
      restaurarEstadoDesdeHistorial(entrada);
      return true;
    }
    return false;
  };

  const navegarAdelante = () => {
    if (
      state.indiceNavegacionActual.value <
      state.historialNavegacion.value.length - 1
    ) {
      state.indiceNavegacionActual.value++;
      const entrada =
        state.historialNavegacion.value[state.indiceNavegacionActual.value];
      restaurarEstadoDesdeHistorial(entrada);
      return true;
    }
    return false;
  };

  const restaurarEstadoDesdeHistorial = (entrada) => {
    // No agregar al historial cuando restauramos
    state.modoAccion.value = entrada.modo;
    state.detalleSeleccionado.value = entrada.detalleSeleccionado;

    // Aplicar el mismo comportamiento que activarModoAccion pero sin agregar al historial
    if (
      entrada.modo === "ver-historial" ||
      entrada.modo === "historia-clinica" ||
      entrada.modo === "expediente-completo"
    ) {
      state.vistaActiva.value = "expediente-completo";
      // NO forzar isLeftPanelCollapsed - dejar que el usuario lo controle
      state.isRightPanelCollapsed.value = true;
      state.isDetailPanelExpanded.value = false;
    } else if (
      entrada.modo === "perfil-paciente" ||
      entrada.modo === "documentos" ||
      entrada.modo === "alertas-paciente"
    ) {
      state.vistaActiva.value = "formulario";
      state.isLeftPanelCollapsed.value = true;
      state.isRightPanelCollapsed.value = false;
      state.isDetailPanelExpanded.value = true;
    } else if (entrada.modo === "nueva-nota") {
      state.vistaActiva.value = "formulario";
      state.isLeftPanelCollapsed.value = true;
      state.isRightPanelCollapsed.value = false;
      state.isDetailPanelExpanded.value = true;
    } else if (entrada.detalleSeleccionado) {
      state.vistaActiva.value = "detalle";
      state.isLeftPanelCollapsed.value = true;
      state.isRightPanelCollapsed.value = false;
      state.isDetailPanelExpanded.value = false;
    } else {
      state.vistaActiva.value = "formulario";
      state.isLeftPanelCollapsed.value = true;
      state.isRightPanelCollapsed.value = false;
      state.isDetailPanelExpanded.value = true;
    }
  };

  const puedeNavegarAtras = computed(
    () => state.indiceNavegacionActual.value > 0
  );
  const puedeNavegarAdelante = computed(
    () =>
      state.indiceNavegacionActual.value <
      state.historialNavegacion.value.length - 1
  );

  const obtenerHistorialReciente = (limite = 10) => {
    // Retornar los elementos más recientes primero (reversed)
    const recientes = [...state.historialNavegacion.value].reverse().slice(0, limite);
    return recientes;
  };

  // Método para inicializar el paciente
  const setPatientId = (id) => {
    state.patientId.value = id;
  };

  // 🔄 Método para inicializar con la vista guardada del paciente
  const inicializarConVistaGuardada = (patientId) => {
    setPatientId(patientId);

    const vistaGuardada = restaurarVistaDelPaciente(patientId);

    if (vistaGuardada && vistaGuardada.modo) {
      // 🆕 Restaurar estado de edición si existe y coincide con el modo actual
      const estadoEdicion = restaurarEstadoEdicion(patientId);
      const options = {};

      if (estadoEdicion && estadoEdicion.modo === vistaGuardada.modo && estadoEdicion.currentId) {
        options.editingState = {
          currentId: estadoEdicion.currentId,
          resourceType: estadoEdicion.resourceType
        };
      }

      // Restaurar la vista guardada pasando el estado de edición
      activarModoAccion(vistaGuardada.modo, vistaGuardada.titulo || '', options);

      // Reconstruir datosEdicion en memoria si se restauró
      if (options.editingState) {
        state.datosEdicion.value = {
          tipo: options.editingState.resourceType,
          id: options.editingState.currentId,
          datos: { id: options.editingState.currentId }
        };
      }
    } else {
      // Vista por defecto si no hay nada guardado
      activarModoAccion('perfil-paciente', 'Perfil del Paciente');
    }
  };

  // Métodos inteligentes para manejar modos de acción
  const activarModoAccion = (modo, titulo = "", options = {}) => {
    // Guardar en historial de navegación
    agregarAlHistorial(modo, titulo);

    // 🔄 Guardar la vista actual en localStorage para este paciente
    if (state.patientId.value) {
      guardarVistaDelPaciente(state.patientId.value, modo, titulo);

      // 📝 Si viene con información de edición, guardar también ese estado
      if (options.editingState && options.editingState.currentId > 0) {
        guardarEstadoEdicion(
          state.patientId.value,
          modo,
          options.editingState.currentId,
          options.editingState.resourceType
        );
      } else {
        // 🗑️ Si no hay estado de edición explícito, limpiar cualquier estado previo
        // Esto asegura que al cambiar a "Nueva Nota" o salir de edición, se limpie el ID
        limpiarEstadoEdicion(state.patientId.value);
        state.datosEdicion.value = null;
      }
    }

    state.modoAccion.value = modo;
    state.detalleSeleccionado.value = null;

    // 🎯 COMPORTAMIENTO INTELIGENTE SEGÚN EL TIPO DE ACCIÓN
    // Mantener BlocAcciones visible en vistas clínicas para navegación fluida

    if (
      modo === "ver-historial" ||
      modo === "historia-clinica" ||
      modo === "expediente-completo"
    ) {
      // 🏥 VISTAS CLÍNICAS: Panel izquierdo colapsado
      state.vistaActiva.value = "expediente-completo";
      state.isLeftPanelCollapsed.value = true;
      state.isRightPanelCollapsed.value = true;
      state.isDetailPanelExpanded.value = false;
    } else if (modo === "perfil-paciente") {
      // 👤 PERFIL DEL PACIENTE: Panel izquierdo colapsado
      state.vistaActiva.value = "formulario";
      state.isLeftPanelCollapsed.value = true;
      state.isRightPanelCollapsed.value = false;
      state.isDetailPanelExpanded.value = true;
    } else if (modo === "documentos" || modo === "alertas-paciente") {
      // 📄 DOCUMENTOS Y ALERTAS: Panel derecho expandido, ocultar BlocAcciones
      state.vistaActiva.value = "formulario";
      state.isLeftPanelCollapsed.value = true;
      state.isRightPanelCollapsed.value = false;
      state.isDetailPanelExpanded.value = true;
    } else if (modo === "nueva-nota" || modo === "editar-nota") {
      // ✏️ NUEVA/EDITAR NOTA: Timeline + detalle expandido, ocultar BlocAcciones
      state.vistaActiva.value = "formulario";
      state.isLeftPanelCollapsed.value = true;
      state.isRightPanelCollapsed.value = false;
      state.isDetailPanelExpanded.value = true;
    } else if (modo === "nueva-receta" || modo === "editar-receta") {
      // 💊 NUEVA/EDITAR RECETA: Mismo comportamiento
      state.vistaActiva.value = "formulario";
      state.isLeftPanelCollapsed.value = true;
      state.isRightPanelCollapsed.value = false;
      state.isDetailPanelExpanded.value = true;
    } else if (modo === "editar-documento") {
      // 📄 EDITAR DOCUMENTO: Mismo comportamiento
      state.vistaActiva.value = "formulario";
      state.isLeftPanelCollapsed.value = true;
      state.isRightPanelCollapsed.value = false;
      state.isDetailPanelExpanded.value = true;
    } else {
      // 📝 OTROS FORMULARIOS: Timeline + panel derecho, ocultar BlocAcciones
      state.vistaActiva.value = "formulario";
      state.isLeftPanelCollapsed.value = true;
      state.isRightPanelCollapsed.value = false;
      state.isDetailPanelExpanded.value = true;
    }
  };

  const cerrarModoAccion = () => {
    state.modoAccion.value = null;
    state.vistaActiva.value = "normal";
    state.isDetailPanelExpanded.value = false;
    state.datosEdicion.value = null; // Limpiar datos de edición

    // 🎯 Panel izquierdo colapsado por defecto
    state.isLeftPanelCollapsed.value = true;
    state.isRightPanelCollapsed.value = true;

    // ⚡ OPTIMIZACIÓN: Invalidar cache
    state._layoutClassesCache = null;
    state._lastLayoutState = null;
  };

  // Método para cargar datos en el formulario de edición
  const cargarDatosEnFormulario = (datos, tipo) => {
    // Guardar los datos en el estado para que los formularios los consuman
    state.datosEdicion.value = {
      tipo,
      datos: markRaw(datos),
      id: datos.id
    };
  };

  // Método para resetear todo el estado
  const resetState = () => {
    state.isLeftPanelCollapsed.value = true;
    state.isRightPanelCollapsed.value = true;
    state.isDetailPanelExpanded.value = false;
    state.detalleSeleccionado.value = null;
    state.filtroActual.value = "";
    state.filtroAvanzado.value = null;
    state.patientId.value = null;
    state.modoAccion.value = null;
    state.vistaActiva.value = "normal";
  };

  const cargarDatosPaciente = (datos) => {

    const datosOptimizados = {
      // Datos básicos del paciente
      id: datos.id || null,
      first_name: datos.first_name || "",
      last_name: datos.last_name || "",
      gender: datos.gender || "",
      birthdate: datos.birthdate || datos.date_of_birth || "",
      email: datos.email || "",
      whatsapp: datos.whatsapp || "",
      photo_url: datos.photo_url || null,
      photo: datos.photo || null,
      profile: datos.profile || {
        address: "",
        reference: "",
        responsible_family_member: "",
        family_member_relationship: "",
        emergency_contact: "",
        patient_identification: "",
        internal_comments: "",
        patient_feedback: "",
        updated_at: null,
      },
      historiaClinica: datos.historiaClinica ||
        state.datosPaciente.value?.historiaClinica || {
        antecedentesPersonales: [],
        antecedentesFamiliares: [],
        alergias: [],
        medicamentosActuales: [],
        cirugiasPrevias: [],
        enfermedadesCronicas: [],
      },
      notasMedicas: markRaw(datos.notasMedicas || []),
      recetas: markRaw(datos.recetas || []),
      documentos: markRaw(datos.documentos || []),
      signosVitales: markRaw(datos.signosVitales || []),
    };

    state.datosPaciente.value = datosOptimizados;
  };

  const agregarNotaMedica = (nota) => {
    const nuevoArray = [
      ...state.datosPaciente.value.notasMedicas,
      markRaw(nota),
    ];
    state.datosPaciente.value = {
      ...state.datosPaciente.value,
      notasMedicas: markRaw(nuevoArray),
    };
  };

  const agregarReceta = (receta) => {
    const nuevoArray = [...state.datosPaciente.value.recetas, markRaw(receta)];
    state.datosPaciente.value = {
      ...state.datosPaciente.value,
      recetas: markRaw(nuevoArray),
    };
  };

  const agregarDocumento = (documento) => {
    const nuevoArray = [
      ...state.datosPaciente.value.documentos,
      markRaw(documento),
    ];
    state.datosPaciente.value = {
      ...state.datosPaciente.value,
      documentos: markRaw(nuevoArray),
    };
  };

  return {
    // Estado
    isLeftPanelCollapsed: state.isLeftPanelCollapsed,
    isRightPanelCollapsed: state.isRightPanelCollapsed,
    isDetailPanelExpanded: state.isDetailPanelExpanded,
    detailExpansionLevel: state.detailExpansionLevel,
    detalleSeleccionado: state.detalleSeleccionado,
    filtroActual: state.filtroActual,
    filtroAvanzado: state.filtroAvanzado,
    patientId: state.patientId,
    modoAccion: state.modoAccion,
    vistaActiva: state.vistaActiva,
    historialNavegacion: state.historialNavegacion,
    indiceNavegacionActual: state.indiceNavegacionActual,
    datosPaciente: state.datosPaciente, // 🆕
    datosEdicion: state.datosEdicion, // 🆕 Datos temporales de edición

    // Computed
    layoutClasses,
    puedeNavegarAtras,
    puedeNavegarAdelante,
    pacienteTieneNotas, // 🆕
    pacienteTieneRecetas, // 🆕
    pacienteTieneDocumentos, // 🆕
    pacienteTieneContenido, // 🆕
    esPacienteNuevo, // 🆕

    // Métodos de paneles
    togglePanel,
    collapsePanel,
    expandPanel,

    // Métodos de detalle
    seleccionarNota,
    cerrarDetalle,
    toggleDetailExpansion,

    // Métodos de filtros
    aplicarFiltro,
    aplicarFiltroAvanzado,
    limpiarFiltros,

    // Métodos de modos de acción
    activarModoAccion,
    cerrarModoAccion,
    cargarDatosEnFormulario,

    // Métodos de navegación tipo libro
    navegarAtras,
    navegarAdelante,
    obtenerHistorialReciente,
    obtenerTituloModo,

    // Métodos de inicialización
    setPatientId,
    resetState,
    inicializarConVistaGuardada,

    // Métodos de persistencia de edición
    guardarEstadoEdicion,
    restaurarEstadoEdicion,
    limpiarEstadoEdicion,

    // 🆕 Métodos para manejar datos del paciente
    cargarDatosPaciente,
    cargarPacienteDesdeStore,
    actualizarDatosPaciente,
    agregarNotaMedica,
    agregarReceta,
    agregarDocumento,
  };

  // 🆕 Función para cargar datos del paciente desde el store
  function cargarPacienteDesdeStore() {
    const { getPatientDataById, storeData } = useStore();
    const pacienteId = storeData.value.lastSelectedOrAddedId;


    if (pacienteId) {
      const datosPaciente = getPatientDataById(pacienteId);

      if (datosPaciente) {
        cargarDatosPaciente({
          ...datosPaciente,
          photo_url: datosPaciente.photo_url || null,
          photo: datosPaciente.photo || null
        });
        setPatientId(pacienteId);
        return true;
      }
    }
    return false;
  }

  function actualizarDatosPaciente(nuevosData) {
    const { addPatientData } = useStore();

    const dataCompleta = {
      ...state.datosPaciente.value,
      ...nuevosData,
      notasMedicas: nuevosData.notasMedicas
        ? markRaw([
          ...state.datosPaciente.value.notasMedicas,
          ...(Array.isArray(nuevosData.notasMedicas)
            ? nuevosData.notasMedicas
            : [nuevosData.notasMedicas]),
        ])
        : state.datosPaciente.value.notasMedicas,
      recetas: nuevosData.recetas
        ? markRaw([
          ...state.datosPaciente.value.recetas,
          ...(Array.isArray(nuevosData.recetas)
            ? nuevosData.recetas
            : [nuevosData.recetas]),
        ])
        : state.datosPaciente.value.recetas,
      documentos: nuevosData.documentos
        ? markRaw([
          ...state.datosPaciente.value.documentos,
          ...(Array.isArray(nuevosData.documentos)
            ? nuevosData.documentos
            : [nuevosData.documentos]),
        ])
        : state.datosPaciente.value.documentos,
    };

    // Actualizar el estado interno con shallowRef
    state.datosPaciente.value = dataCompleta;

    // Actualizar el store para persistencia (con throttle en actualizaciones frecuentes)
    throttle(() => {
      addPatientData(dataCompleta);
      // Notificar cambios usando el Event Bus centralizado
      updatePatientData(dataCompleta);
    }, 300)();
  }
}
